import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "@/hooks/useAuth";

/**
 * ReferralLanding — /r/:code
 *
 * Stamps the attribution cookie via /api/referrals/visit, then invites the
 * visitor to sign in. On successful sign-in, the backend's auth-exchange-session
 * reads the kalki_visitor_id cookie and links the attribution — no magic.
 */
export default function ReferralLanding() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user, loading, login } = useAuth();
  const [status, setStatus] = useState("resolving");
  const [referrerName, setReferrerName] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/referrals/visit", { code });
        setStatus(data?.ok ? "ok" : "unknown_code");
        setReferrerName(data?.referrer_name || null);
      } catch (e) {
        setStatus("unknown_code");
      }
    })();
  }, [code]);

  // Already signed in? Try to link now, then route to app.
  useEffect(() => {
    if (loading || !user) return;
    (async () => {
      try { await api.post("/referrals/link", { code }); } catch (_) {}
      nav("/app");
    })();
  }, [loading, user, code, nav]);

  if (loading) return null;

  return (
    <div style={{ minHeight: "100dvh", background: "#0b0b10", color: "#F5F5F7", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 520, textAlign: "center" }} data-testid="referral-landing">
        <div style={{ fontSize: 11, letterSpacing: ".4em", color: "#9D9DB0", textTransform: "uppercase", marginBottom: 20 }}>KALKI · superlife.living</div>
        {status === "unknown_code" ? (
          <>
            <h1 style={{ fontSize: 36, letterSpacing: "-0.5px", margin: 0 }}>This invitation link isn't recognized.</h1>
            <p style={{ color: "#A1A1AA", marginTop: 16 }}>Ask your friend to resend their link, or begin your journey directly.</p>
            <button data-testid="referral-begin" onClick={() => nav("/")} style={{ marginTop: 28, padding: "12px 24px", background: "linear-gradient(135deg,#5D00FF,#FF2E9A)", color: "white", border: 0, borderRadius: 999 }}>
              Begin on KALKI
            </button>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 42, letterSpacing: "-0.5px", margin: 0 }}>Begin your journey with <em style={{ fontStyle: "normal", background: "linear-gradient(135deg,#5D00FF,#FF2E9A)", WebkitBackgroundClip: "text", color: "transparent" }}>KALKI.</em></h1>
            <p style={{ color: "#D5D5DF", marginTop: 20, lineHeight: 1.6, fontSize: 17 }}>
              <strong>100 Welcome Credits</strong> &nbsp;+&nbsp; <strong>50 Referral Credits</strong>
            </p>
            <p style={{ color: "#A1A1AA", marginTop: 8, fontSize: 13.5, lineHeight: 1.6 }}>
              150 KALKI Credits for eligible new devotees after you create your account and complete your birth onboarding — enough to converse with KALKI, see your Vedic sky and unlock a Life Report.
            </p>
            <button
              data-testid="referral-signin"
              onClick={() => { sessionStorage.setItem("kalki_intent", "SIGNUP"); login(); }}
              style={{ marginTop: 28, padding: "14px 28px", fontSize: 15, letterSpacing: ".15em", background: "linear-gradient(135deg,#5D00FF,#FF2E9A)", color: "white", border: 0, borderRadius: 999, textTransform: "uppercase" }}
            >
              Continue with Google
            </button>
            <a
              data-testid="referral-create-account"
              href={`/?signin=1&ref=${encodeURIComponent(code || "")}`}
              style={{ marginTop: 12, padding: "12px 24px", fontSize: 13, letterSpacing: ".1em", background: "rgba(93,0,255,0.06)", color: "#5D00FF", border: "1px solid rgba(93,0,255,0.25)", borderRadius: 999, display: "inline-block" }}
            >
              Create account with email
            </a>
            <p style={{ color: "#676773", marginTop: 24, fontSize: 11.5, letterSpacing: ".1em" }}>Referral code: {code?.toUpperCase()}</p>
          </>
        )}
      </div>
    </div>
  );
}
